import { RotateCcw } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { type GraphNode as Node, NODE_W } from './layout'
import { cardQuestion, TERMINAL } from './tree'

export enum NodeState {
  /** The step the detail panel is showing. */
  SELECTED = 'SELECTED',
  /** One hop from the selected step, either direction. */
  ADJACENT = 'ADJACENT',
  RESTING = 'RESTING',
  /** Something else is selected and this isn't connected to it. */
  MUTED = 'MUTED',
}

interface GraphNodeProps {
  node: Node
  state: NodeState
  onSelect: (service: string) => void
}

/** One question the customer is asked, as a card on the canvas. */
export function GraphNode({ node, state, onSelect }: GraphNodeProps) {
  const isEnd = node.service === TERMINAL

  return (
    <Button
      variant="outline"
      onClick={() => onSelect(node.service)}
      style={{ left: node.x, top: node.y, width: NODE_W, height: node.h }}
      className={cn(
        // A card, not a control: unpick the button's centred single-line layout.
        'absolute flex flex-col items-start justify-start gap-1.5 rounded-sm border p-3 text-left font-normal whitespace-normal',
        isEnd ? 'bg-brand-100 border-brand-300' : 'bg-surface border-ink-200',
        state === NodeState.SELECTED && 'border-ink ring-ink/10 shadow-sm ring-2',
        state === NodeState.ADJACENT && 'border-brand-600',
        state === NodeState.MUTED && 'opacity-35',
        state === NodeState.RESTING && 'hover:border-ink-400'
      )}
    >
      <div className="flex w-full items-center gap-1.5">
        <span className="bg-ink-100 text-ink-500 shrink-0 rounded-sm px-1 font-mono text-[9px]">
          {String(node.stagePosition).padStart(2, '0')}
        </span>
        <span className="text-ink-400 truncate text-[10px]">{node.stage?.name}</span>
        {node.selfLoops.length > 0 && (
          <span
            className="text-ink-400 ml-auto flex shrink-0 items-center gap-0.5 text-[9px]"
            title={`${node.selfLoops.length} answer(s) re-ask this question`}
          >
            <RotateCcw className="size-2.5" />
            {node.selfLoops.length}
          </span>
        )}
      </div>

      {node.step ? (
        <>
          <span className="text-ink flex flex-col gap-1 text-[12px] leading-snug font-medium">
            {cardQuestion(node.step).map((text) => (
              <span key={text}>{text}</span>
            ))}
          </span>
          <span className="text-ink-500 mt-auto shrink-0 text-[10px]">{node.step.collected}</span>
        </>
      ) : (
        <span className="text-brand-800 text-[12px] font-medium">Application complete</span>
      )}
    </Button>
  )
}
